/**
 * ChoiceSet (Foundry: rule-element/choice-set): a prompt whose answer other Rule Elements on the
 * same Entry read through `{item|flags.pf2e.rulesSelections.<flag>}`. Foundry asks at item
 * creation; here the answer lives on the Choice that holds the Entry (`answers[flag]`), and the
 * engine reports every ChoiceSet as a prompt with its candidates so the guide (#9) can ask.
 *
 * With no answer (and `allowNoSelection` off) every Rule Element on the Entry is inactive until
 * one is made, as in Foundry.
 */
import type { AnswerValue } from "../../character/model";
import { recordItemType, recordMaxTakable, recordRollOptions } from "../catalogue";
import { getProperty } from "../actor-data";
import type { EngineItem } from "../items";
import { isValidPredicate, testPredicate, type RawPredicate } from "../predicate";
import { resolveInjectedProperties } from "../resolve";
import { sluggify, sluggifyCamel } from "../slug";
import type { EngineState } from "../state";
import { PHYSICAL_ITEM_TYPES } from "../values";
import type { RuleHandler, RuleInstance } from "./base";

export type ChoiceForm = "array" | "config" | "path" | "owned-items" | "attacks" | "pack";

export interface Candidate {
  value: AnswerValue;
  label: string;
  /** The Entry's UUID when the candidate is an Entry (Pack queries, UUID arrays). */
  uuid: string | null;
  /** Remaster status when the candidate is an Entry; Legacy candidates are legal (docs/design.md). */
  remaster: boolean | null;
}

export interface ChoiceSetData {
  choices: unknown;
  form: ChoiceForm | null;
  flag: string;
  prompt: string;
  adjustName: boolean | string;
  allowNoSelection: boolean;
  allowedDrops: { label: string | null; predicate: RawPredicate } | null;
  rollOption: string | null;
  actorFlag: boolean;
  selection: AnswerValue | null;
  /** True when the Entry has no answer and needs one: its Rule Elements are inactive. */
  open: boolean;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isItemUuid(value: unknown): value is string {
  return typeof value === "string" && /^Compendium\.[^.]+\.[^.]+\.Item\.[^.]+$/.test(value);
}

function isAnswerValue(value: unknown): value is AnswerValue {
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean" || isPlainObject(value);
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = Object.keys(a);
    return keys.length === Object.keys(b).length && keys.every((k) => deepEqual(a[k], b[k]));
  }
  return false;
}

/** The form a `choices` value takes, or null when it is none of Foundry's. */
export function choiceForm(choices: unknown): ChoiceForm | null {
  if (Array.isArray(choices)) return "array";
  if (typeof choices === "string") return "path";
  if (!isPlainObject(choices)) return null;
  if (typeof choices.config === "string") return "config";
  if (choices.ownedItems) return "owned-items";
  if (choices.attacks || choices.unarmedAttacks) return "attacks";
  if ("filter" in choices) return "pack";
  return null;
}

function defaultFlag(rule: RuleInstance): string {
  return sluggifyCamel(rule.slug ?? rule.item.slug ?? rule.item.name);
}

function parse(rule: RuleInstance, state: EngineState): void {
  const s = rule.source;
  const form = choiceForm(s.choices);
  if (form === null) return rule.fail("choices: must be an array, an object, or a string");
  if (form === "owned-items" && !(Array.isArray((s.choices as Record<string, unknown>).types) && ((s.choices as Record<string, unknown>).types as unknown[]).length > 0)) {
    return rule.fail("choices: `types` is required if `ownedItems` is set");
  }
  if (form === "pack") {
    const filter = (s.choices as Record<string, unknown>).filter;
    if (!isValidPredicate(filter) || filter.length === 0) return rule.fail("choices.filter: must be an array with at least one statement");
  }
  if (isPlainObject(s.choices) && "query" in s.choices) return rule.fail("choice set queries are no longer supported (Foundry v11)");
  const flag = typeof s.flag === "string" && s.flag.length > 0 ? s.flag.replace(/[^-a-z0-9]/gi, "") : defaultFlag(rule);
  if (s.adjustName !== undefined && typeof s.adjustName !== "boolean" && typeof s.adjustName !== "string") return rule.fail("adjustName: must be a boolean or a string");
  let allowedDrops: ChoiceSetData["allowedDrops"] = null;
  if (s.allowedDrops !== undefined && s.allowedDrops !== null) {
    if (!isPlainObject(s.allowedDrops)) return rule.fail("allowedDrops: must be an object");
    const predicate = s.allowedDrops.predicate ?? [];
    if (!isValidPredicate(predicate)) return rule.fail("allowedDrops.predicate: malformed");
    allowedDrops = { label: typeof s.allowedDrops.label === "string" ? s.allowedDrops.label : null, predicate };
  }
  // An empty string is no answer.
  const answered = rule.item.answers[flag];
  const selection: AnswerValue | null = isAnswerValue(answered) && answered !== "" ? answered : isAnswerValue(s.selection) && s.selection !== "" ? s.selection : null;
  const data: ChoiceSetData = {
    choices: s.choices,
    form,
    flag,
    prompt: typeof s.prompt === "string" && s.prompt ? s.prompt : "PF2E.UI.RuleElements.ChoiceSet.Prompt",
    adjustName: s.adjustName === undefined ? true : (s.adjustName as boolean | string),
    allowNoSelection: s.allowNoSelection === true,
    allowedDrops,
    rollOption: typeof s.rollOption === "string" && s.rollOption ? s.rollOption : rule.slug,
    actorFlag: s.actorFlag === true,
    selection,
    open: false,
  };
  rule.data = data as unknown as Record<string, unknown>;

  if (selection === null) {
    // Foundry: ignore this and every sibling Rule Element until a selection is made.
    if (!data.allowNoSelection && rule.test(state)) {
      data.open = true;
      rule.ignored = true;
    }
    return;
  }
  const selections = ((rule.item.flags.pf2e.rulesSelections as Record<string, unknown> | undefined) ??= {});
  selections[flag] = selection;
  if (data.actorFlag) state.actor.flags.pf2e[flag] = selection;
  setRollOption(rule, state, data);
  adjustName(rule, state, data);
}

function setRollOption(rule: RuleInstance, state: EngineState, data: ChoiceSetData): void {
  const selection = data.selection;
  if (!data.rollOption || !(typeof selection === "string" || typeof selection === "number")) return;
  // A UUID selection gets the Entry's slug as its suffix, as Foundry appends at item creation.
  const suffix = isItemUuid(selection) ? entrySlug(state, selection) : String(selection);
  if (suffix === null) {
    rule.problems.push(`rollOption: the Entry ${selection} is not loaded, so its slug is unknown`);
    return;
  }
  const option = `${data.rollOption}:${suffix}`;
  state.rollOptions.set("all", option);
  rule.item.specialOptions.push(option);
}

function entrySlug(state: EngineState, uuid: string): string | null {
  const entry = state.entries.get(uuid);
  if (entry) return entry.slug || sluggify(entry.name);
  const record = state.catalogue?.byUuid(uuid);
  return record ? record.s : null;
}

function entryName(state: EngineState, uuid: string): string | null {
  return state.entries.get(uuid)?.name ?? state.catalogue?.byUuid(uuid)?.n ?? null;
}

function localize(state: EngineState, key: string): string {
  return state.catalogue?.localize(key) ?? key;
}

/** Foundry's `#adjustName`: rename the Entry after its selection. */
function adjustName(rule: RuleInstance, state: EngineState, data: ChoiceSetData): void {
  if (data.adjustName === false || data.selection === null) return;
  const label = selectionLabel(rule, state, data);
  if (label === null) return;
  const item = rule.item;
  if (data.adjustName === true) {
    const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    item.name = `${item.name} (${label})`.replace(new RegExp(`\\(${escaped}\\) \\(${escaped}\\)$`), `(${label})`);
    return;
  }
  const replacements: Record<string, string> = { [data.flag]: label };
  for (const other of rule.item.rules) {
    if (other.key !== "ChoiceSet" || !Array.isArray(other.choices) || other === rule.source) continue;
    const flag = typeof other.flag === "string" && other.flag ? other.flag.replace(/[^-a-z0-9]/gi, "") : null;
    const value = flag ? rule.item.answers[flag] : undefined;
    const choice = (other.choices as unknown[]).find((c) => isPlainObject(c) && deepEqual(c.value, value));
    if (flag && isPlainObject(choice) && typeof choice.label === "string") replacements[flag] = localize(state, choice.label);
  }
  item.name = localize(state, data.adjustName).replace(/\{([^}]+)\}/g, (whole, key: string) => replacements[key] ?? whole);
}

/** The label of the current selection, when it can be found without a full candidate query. */
export function selectionLabel(rule: RuleInstance, state: EngineState, data: ChoiceSetData): string | null {
  const selection = data.selection;
  if (selection === null) return null;
  if (isItemUuid(selection)) return entryName(state, selection);
  if (data.form === "array") {
    const choice = (data.choices as unknown[]).find((c) => isPlainObject(c) && deepEqual(c.value, selection));
    return isPlainObject(choice) && typeof choice.label === "string" ? localize(state, choice.label) : typeof selection === "string" ? selection : null;
  }
  if (data.form === "config" || data.form === "path") {
    const table = tableFor(state, data);
    const entry = table?.[String(selection)];
    const label = typeof entry === "string" ? entry : isPlainObject(entry) && typeof entry.label === "string" ? entry.label : null;
    return label ? localize(state, label) : typeof selection === "string" ? selection : null;
  }
  if (data.form === "pack" && typeof selection === "string") {
    const itemType = packItemType(data);
    const record = state.catalogue?.records.find((r) => r.t === itemType && r.s === selection);
    return record?.n ?? selection;
  }
  if (data.form === "owned-items" && typeof selection === "string") return state.items.find((i) => i.id === selection)?.name ?? selection;
  return typeof selection === "string" ? selection : null;
}

function packItemType(data: ChoiceSetData): string {
  const itemType = (data.choices as Record<string, unknown>).itemType;
  return typeof itemType === "string" && itemType ? itemType : "feat";
}

/** The config table (or actor path record) a `config` or string-path ChoiceSet draws from. */
function tableFor(state: EngineState, data: ChoiceSetData): Record<string, unknown> | undefined {
  const path = data.form === "config" ? String((data.choices as Record<string, unknown>).config) : String(data.choices);
  const table = state.catalogue?.table(path);
  if (table) return table;
  const fromActor = getProperty(state.actorView, path);
  return isPlainObject(fromActor) || Array.isArray(fromActor) ? (fromActor as Record<string, unknown>) : undefined;
}

/** Foundry's `processChoicesFromData`. */
function choicesFromData(data: unknown): { value: string; label: string; predicate?: unknown }[] {
  if (Array.isArray(data)) {
    return data.every((c) => isPlainObject(c) && typeof c.value === "string") ? (data as { value: string; label: string }[]) : [];
  }
  if (isPlainObject(data)) {
    const entries = Object.entries(data);
    if (!entries.every(([, c]) => typeof (isPlainObject(c) ? c.label : c) === "string")) return [];
    return entries.map(([key, entryValue]) => {
      const choice = typeof entryValue === "string" ? { label: entryValue } : (entryValue as Record<string, unknown>);
      return { ...choice, label: String(choice.label), value: key };
    });
  }
  return [];
}

export interface Inflated {
  candidates: Candidate[];
  /** Null when this form cannot be computed yet (no catalogue; Strikes and equipment from later issues). */
  available: boolean;
  note: string | null;
}

/** Roll options Foundry tests ChoiceSet predicates against: the actor's plus the Entry's `parent:` options. */
function promptOptions(rule: RuleInstance, state: EngineState): Set<string> {
  return new Set([...state.rollOptions.get(), ...rule.item.getRollOptions("parent")]);
}

function candidate(value: AnswerValue, label: string, state: EngineState): Candidate {
  if (isItemUuid(value)) {
    const record = state.catalogue?.byUuid(value);
    const entry = state.entries.get(value);
    return { value, label: entry?.name ?? record?.n ?? label, uuid: value, remaster: entry ? entry.publication.remaster : (record?.rm ?? null) };
  }
  return { value, label, uuid: null, remaster: null };
}

/**
 * Foundry's `inflateChoices`: the candidate list for the prompt. `validate` applies the per-choice
 * predicates (skipped for a preselected answer, as upstream does).
 */
export function inflateChoices(rule: RuleInstance, state: EngineState, data: ChoiceSetData, validate: boolean): Inflated {
  const options = promptOptions(rule, state);
  const test = (predicate: unknown, injectables?: Record<string, unknown>): boolean => {
    if (predicate === undefined || predicate === null) return true;
    if (!isValidPredicate(predicate)) return false;
    const resolved = resolveInjectedProperties(rule, state, structuredClone(predicate), injectables ? { warn: false, injectables } : { warn: false });
    return testPredicate(resolved, options);
  };
  const sorted = (list: Candidate[]): Candidate[] => list.sort((a, b) => a.label.localeCompare(b.label));

  switch (data.form) {
    case "array": {
      const list = (data.choices as unknown[]).filter(isPlainObject).filter((c) => isAnswerValue(c.value));
      const kept = validate ? list.filter((c) => test(c.predicate)) : list;
      return { candidates: kept.map((c) => candidate(c.value as AnswerValue, typeof c.label === "string" ? localize(state, c.label) : String(c.value), state)), available: true, note: null };
    }
    case "config":
    case "path": {
      const table = tableFor(state, data);
      if (!table) {
        const path = data.form === "config" ? String((data.choices as Record<string, unknown>).config) : String(data.choices);
        return { candidates: [], available: false, note: `no table or actor data at "${path}"` };
      }
      const choices = choicesFromData(table);
      const shared = data.form === "config" ? (data.choices as Record<string, unknown>).predicate : undefined;
      const kept = validate ? choices.filter((choice) => test(choice.predicate ?? structuredClone(shared ?? []), { choice })) : choices;
      return { candidates: sorted(kept.map((c) => candidate(c.value, localize(state, c.label), state))), available: true, note: null };
    }
    case "owned-items": {
      const spec = data.choices as Record<string, unknown>;
      const types = (spec.types as unknown[]).filter((t): t is string => typeof t === "string");
      const isOfType = (item: EngineItem): boolean => types.includes(item.type) || (types.includes("physical") && (PHYSICAL_ITEM_TYPES as readonly string[]).includes(item.type));
      const predicate = isValidPredicate(spec.predicate) ? resolveInjectedProperties(rule, state, structuredClone(spec.predicate), { warn: false }) : [];
      const owned = state.items.filter((i) => isOfType(i) && !(i.type === "weapon" && i.category === "unarmed") && testPredicate(predicate, new Set([...options, ...i.getRollOptions("item")])));
      const candidates = sorted(owned.map((i) => ({ value: i.id, label: i.name, uuid: i.uuid, remaster: i.entry.publication.remaster })));
      const physicalPresent = state.items.some((i) => (PHYSICAL_ITEM_TYPES as readonly string[]).includes(i.type));
      return { candidates, available: physicalPresent, note: physicalPresent ? null : "equipment arrives with the inventory (#11)" };
    }
    case "attacks":
      return { candidates: [], available: false, note: "Strikes arrive with the Strike Rule Element (#8)" };
    case "pack":
      return packQuery(rule, state, data, options);
    default:
      return { candidates: [], available: false, note: "unknown choices form" };
  }
}

/** Foundry's `queryCompendium` over the catalogue index. */
function packQuery(rule: RuleInstance, state: EngineState, data: ChoiceSetData, options: Set<string>): Inflated {
  const catalogue = state.catalogue;
  if (!catalogue) return { candidates: [], available: false, note: "no catalogue was supplied to the engine" };
  const spec = data.choices as Record<string, unknown>;
  const filter = resolveInjectedProperties(rule, state, structuredClone(spec.filter as RawPredicate), { warn: false });
  if (!isValidPredicate(filter)) return { candidates: [], available: true, note: "filter could not be resolved" };
  const itemType = packItemType(data);
  const pack = typeof spec.pack === "string" ? spec.pack : null;
  const slugsAsValues = spec.slugsAsValues === true;
  const parentOptions = rule.item.getRollOptions("parent");
  const owned = new Map<string, number>();
  for (const item of state.items) {
    if (item.type !== "feat") continue;
    owned.set(item.slug, (owned.get(item.slug) ?? 0) + 1);
  }
  const heritages = new Set(state.items.filter((i) => i.type === "heritage").map((i) => i.slug));
  const candidates: Candidate[] = [];
  for (const record of catalogue.records) {
    if (record.t !== itemType || (pack && record.p !== pack)) continue;
    if (!testPredicate(filter, new Set([...recordRollOptions(record), ...parentOptions, ...options]))) continue;
    if (record.t === "feat" && (owned.get(record.s) ?? 0) >= recordMaxTakable(record)) continue;
    if (record.t === "heritage" && heritages.has(record.s)) continue;
    candidates.push({ value: slugsAsValues ? record.s : record.u, label: record.n, uuid: record.u, remaster: record.rm });
  }
  return { candidates: candidates.sort((a, b) => a.label.localeCompare(b.label)), available: true, note: null };
}

/** Whether the current answer is among the candidates; null when that cannot be checked. */
export function validateSelection(rule: RuleInstance, state: EngineState, data: ChoiceSetData): { legal: boolean | null; note: string | null } {
  const selection = data.selection;
  if (selection === null) return { legal: null, note: null };
  if (rule.item.preselected.has(data.flag)) return { legal: true, note: "preselected by the granting Entry" };
  if (data.form === "pack") {
    const catalogue = state.catalogue;
    if (!catalogue || typeof selection !== "string") return { legal: null, note: catalogue ? "answer is not an Entry" : "no catalogue was supplied to the engine" };
    const spec = data.choices as Record<string, unknown>;
    const itemType = packItemType(data);
    const record = spec.slugsAsValues === true ? catalogue.records.find((r) => r.t === itemType && r.s === selection) : catalogue.byUuid(selection);
    if (!record) return { legal: null, note: `${selection} is not in the catalogue` };
    if (recordItemType(record) !== itemType && record.t !== itemType) return { legal: false, note: `${record.n} is a ${record.t}, not a ${itemType}` };
    const filter = resolveInjectedProperties(rule, state, structuredClone(spec.filter as RawPredicate), { warn: false });
    const options = new Set([...recordRollOptions(record), ...rule.item.getRollOptions("parent"), ...promptOptions(rule, state)]);
    const legal = isValidPredicate(filter) && testPredicate(filter, options);
    return { legal, note: legal ? null : `${record.n} does not pass the filter` };
  }
  const inflated = inflateChoices(rule, state, data, true);
  if (!inflated.available) return { legal: null, note: inflated.note };
  const legal = inflated.candidates.some((c) => deepEqual(c.value, selection));
  return { legal, note: legal ? null : "the answer is not among the candidates" };
}

export function isChoiceSetOpen(rule: RuleInstance): boolean {
  return rule.key === "ChoiceSet" && (rule.data as unknown as ChoiceSetData).open === true;
}

export const ChoiceSetHandler: RuleHandler = { key: "ChoiceSet", parse };
